import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import {
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';
import { AppState, useAppDispatch } from 'stores/type';
import { showDialog, closeDialog } from 'stores/dialogSlice';
import { localeIds } from 'locals';
import { Application } from 'models/Application/type';
import { ApplicationDomain } from 'models/Application';
import { AvailableApplication } from 'models/Device/type';
import { setErrorMessageLocaleId, setExceptionError } from 'stores/errorSlice';
import { MONTHLY_TRANSFER_LIMIT_EXCEEDED_MESSAGE } from 'repositories/Error';
import { DefaultTemplate } from 'views/templates/DefaultTemplate';
import { Splash } from 'views/components/molecules/Splash';
import { AppDetailPageLayout } from 'views/layouts/AppDetailPageLayout';
import { ROUTER_PATH } from 'views/constants';
import { getRequestPath } from 'utils/string';
import { useDevice } from 'views/hooks/useDevice';

interface AppDetailPageParams {
  packageName: string;
  revision: string;
  userName: string;
  groupName: string;
}

export const AppDetailPage: React.FC = () => {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const useSelector: TypedUseSelectorHook<AppState> = useReduxSelector;
  const device = useSelector((state) => state.deviceState.device);
  const [application, setApplication] = useState<Application | undefined>(
    undefined
  );
  const [installSessionToken, setInstallSessionToken] = useState<
    string | undefined
  >(undefined);
  const [otherApplications, setOtherApplications] = useState<
    AvailableApplication[]
  >([]);
  const [nextMaxRevision, setNextMaxRevision] = useState<number | undefined>(
    undefined
  );
  const [startInstall, setStartInstall] = useState(false);
  const [startOverwriteInstall, setStartOverwriteInstall] = useState(false);
  const [transferLimitExceeded, setTransferLimitExceeded] = useState(false);
  const {
    userName,
    groupName,
    packageName,
    revision,
  } = useParams<AppDetailPageParams>();
  const applicationDomain = useMemo(() => {
    const ownerType = userName ? 'users' : 'organizations';
    const ownerName = userName ? userName : groupName;

    return new ApplicationDomain(
      ownerType,
      ownerName,
      packageName,
      revision,
      device
    );
  }, [device, groupName, packageName, revision, userName]);
  const { reInstallConfigurationProfile } = useDevice();

  const handleCloseDialogAndGoBack = useCallback(() => {
    dispatch(closeDialog());
    history.goBack();
  }, [history, dispatch]);

  const handleClickSearch = useCallback((): void => {
    const searchPath = userName
      ? getRequestPath(ROUTER_PATH.userAppSearch, {
          userName,
          packageName,
          revision,
        })
      : getRequestPath(ROUTER_PATH.groupAppSearch, {
          groupName,
          packageName,
          revision,
        });

    history.push(searchPath);
  }, [history, packageName, revision, userName, groupName]);

  const handleAlertWithGoBack = useCallback(
    (id: string, values?: Record<string, string>) => {
      dispatch(
        showDialog({
          content: intl.formatMessage({ id }, values),
          defaultAction: {
            label: intl.formatMessage({ id: localeIds.dialog.ok }),
            callback: handleCloseDialogAndGoBack,
          },
        })
      );
    },
    [dispatch, handleCloseDialogAndGoBack, intl]
  );

  const callOtherVersion = useCallback(
    async (maxRevision: number | undefined): Promise<number | undefined> => {
      return await applicationDomain
        .applications(maxRevision)
        .then((result) => {
          if (result.isFailure()) {
            handleAlertWithGoBack(result.value.localeId());
            return undefined;
          }

          const [availableApplications, nextMaxRevision] = result.value;
          setOtherApplications((state) => {
            return state.concat(availableApplications);
          });
          setNextMaxRevision(nextMaxRevision);
          return nextMaxRevision;
        })
        .catch((e) => {
          dispatch(setExceptionError(e));
          return undefined;
        });
    },
    [applicationDomain, dispatch, handleAlertWithGoBack]
  );

  const handleShowOtherVersion = useCallback(async (): Promise<void> => {
    await callOtherVersion(nextMaxRevision);
  }, [callOtherVersion, nextMaxRevision]);

  const binaryInstall = useCallback(
    async (overwrite: boolean, application: Application): Promise<void> => {
      applicationDomain
        .install(installSessionToken as string, overwrite, application)
        .then((result) => {
          if (result.isFailure()) {
            dispatch(setErrorMessageLocaleId(result.value.localeId()));
            return;
          }

          dispatch(
            showDialog({
              content: intl.formatMessage({
                id: localeIds.alert.startBinaryInstall,
              }),
            })
          );
          if (overwrite || device?.detail?.mustOverwriteInstall) {
            setStartOverwriteInstall(true);
          } else {
            setStartInstall(true);
          }
        })
        .catch((e) => {
          dispatch(setExceptionError(e));
        });
    },
    [
      applicationDomain,
      installSessionToken,
      dispatch,
      intl,
      device?.detail?.mustOverwriteInstall,
    ]
  );

  const handleCloseDialog = useCallback(() => {
    dispatch(closeDialog());
  }, [dispatch]);

  const reInstallProfile = useCallback(() => {
    dispatch(
      showDialog({
        content: intl.formatMessage({
          id: localeIds.configurationProfileInfo.confirm.reInstall,
        }),
        defaultAction: {
          label: intl.formatMessage({ id: localeIds.dialog.ok }),
          callback: async () => {
            dispatch(closeDialog());
            await reInstallConfigurationProfile().catch((e) => {
              dispatch(setExceptionError(e));
            });
          },
        },
        cancelAction: {
          label: intl.formatMessage({ id: localeIds.dialog.cancel }),
          callback: handleCloseDialog,
        },
      })
    );
  }, [dispatch, handleCloseDialog, intl, reInstallConfigurationProfile]);

  const showTransferLimitExceededDialog = useCallback(() => {
    dispatch(
      showDialog({
        content: intl.formatMessage({
          id: localeIds.api.error.monthlyTransferLimitExceeded,
        }),
        defaultAction: {
          label: intl.formatMessage({ id: localeIds.dialog.ok }),
          callback: handleCloseDialog,
        },
      })
    );
  }, [dispatch, intl, handleCloseDialog]);

  const performInstall = useCallback(
    (overwrite: boolean) => {
      if (transferLimitExceeded) {
        showTransferLimitExceededDialog();
        return;
      }
      binaryInstall(overwrite, application as Application);
    },
    [
      binaryInstall,
      application,
      transferLimitExceeded,
      showTransferLimitExceededDialog,
    ]
  );

  const handleInstall = useCallback(() => performInstall(false), [
    performInstall,
  ]);

  const handleOverwriteInstall = useCallback(() => performInstall(true), [
    performInstall,
  ]);

  const handleUnsupportedOverwriteInstall = useCallback(() => {
    if (device?.detail) {
      handleAlertWithGoBack(localeIds.appInstall.unsupportedOverwriteInstall, {
        deviceOsVersion: device.detail.osVersion,
      });
    }
  }, [device, handleAlertWithGoBack]);

  const handleReInstallProfile = useCallback(() => {
    reInstallProfile();
  }, [reInstallProfile]);

  useEffect(() => {
    // インストールボタンタップ時にセッションを作ると不要なダイアログが出るので、
    // そのダイアログが出ないようにワークアラウンドで画面表示にセッションを作る
    applicationDomain
      .installSessionToken()
      .then((result) => {
        if (result.isFailure()) {
          // 月間転送量上限エラーの場合はインストールボタンタップ時に表示
          if (
            result.value.originalMessage ===
            MONTHLY_TRANSFER_LIMIT_EXCEEDED_MESSAGE
          ) {
            setTransferLimitExceeded(true);
            return;
          }
          handleAlertWithGoBack(result.value.localeId());
          return;
        }

        setInstallSessionToken(result.value);
      })
      .catch((e) => {
        dispatch(setExceptionError(e));
      });

    applicationDomain
      .detail()
      .then((result) => {
        if (result.isFailure()) {
          handleAlertWithGoBack(result.value.localeId());
          return;
        }

        setApplication(result.value);
      })
      .catch((e) => {
        dispatch(setExceptionError(e));
      });

    setOtherApplications([]);
    callOtherVersion(undefined);
  }, [
    applicationDomain,
    callOtherVersion,
    dispatch,
    handleAlertWithGoBack,
    handleCloseDialog,
    intl,
  ]);

  return device?.detail && application && otherApplications ? (
    <DefaultTemplate
      headerText={intl.formatMessage({
        id: localeIds.appDetailPageLayout.title,
      })}
      onBack={(): void => {
        history.push(ROUTER_PATH.apps);
      }}
    >
      <AppDetailPageLayout
        deviceDetail={device.detail}
        application={application}
        otherApplications={otherApplications}
        existOtherVersion={nextMaxRevision !== undefined}
        startInstall={startInstall}
        startOverwriteInstall={startOverwriteInstall}
        onInstall={handleInstall}
        onOverwriteInstall={handleOverwriteInstall}
        onUnsupportedOverwriteInstall={handleUnsupportedOverwriteInstall}
        onShowOtherVersion={handleShowOtherVersion}
        installReady={!!installSessionToken || transferLimitExceeded}
        onReInstallProfile={handleReInstallProfile}
        onClickSearch={handleClickSearch}
      />
    </DefaultTemplate>
  ) : (
    <DefaultTemplate headerText="">
      <Splash />
    </DefaultTemplate>
  );
};
